<template>
  <v-col class="py-0" cols="4">
    <v-row style="margin: 0; margin-left: -12px; margin-right: -12px">
      <v-col class="py-0">
        <v-autocomplete
          :items="fromPlaces"
          :item-value="isAdvertisedPlaces ? 'key' : 'short_name'"
          :item-text="item => item.short_name"
          :filter="
            (item, query) =>
              item.short_name.toLowerCase().includes(query.toLowerCase()) ||
              item.stop_name.toLowerCase().includes(query.toLowerCase())
          "
          label="Från"
          :item-disabled="
            v => v.key === `${item.text.to}_${+new Date(item.text.arrival)}`
          "
          :rules="[
            !isChecked && validatingForm ? () => true : required,
            placeRule
          ]"
          :loading="loading"
          v-model="replacement_from_place"
          :append-icon="isDiff('from') ? 'mdi-alert-octagram' : 'mdi-menu-down'"
          :disabled="item.missing || isHistoryEvent || disableFirstPlace"
        />
      </v-col>
      <v-col class="py-0">
        <v-autocomplete
          :items="toPlaces"
          :item-value="isAdvertisedPlaces ? 'key' : 'short_name'"
          :item-text="item => item.short_name"
          :filter="
            (item, query) =>
              item.short_name.toLowerCase().includes(query.toLowerCase()) ||
              item.stop_name.toLowerCase().includes(query.toLowerCase())
          "
          label="Till"
          :item-disabled="
            v => v.key === `${item.text.from}_${+new Date(item.text.departure)}`
          "
          :rules="[!isChecked && validatingForm ? () => true : required]"
          :loading="loading"
          v-model="replacement_to_place"
          :append-icon="isDiff('to') ? 'mdi-alert-octagram' : 'mdi-menu-down'"
          :disabled="item.missing || isHistoryEvent || disableLastPlace"
        />
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { required } from '@/common/validationRules'
import { format } from 'date-fns'
import { get } from 'vuex-pathify'

const getSet = prop => ({
  get() {
    const section = this.form[this.uuid].data.cancel_sections[this.index]

    if (!this.isAdvertisedPlaces) return section[prop]

    const advertisedProp = prop === 'to' ? 'arrival' : 'departure'
    if (!section[prop] || !section[advertisedProp]) return null
    return `${section[prop]}_${section[advertisedProp]}`
  },

  set(v) {
    let value = v
    if (!value || !value.length) {
      value = ''
    }

    const place = value.split('_')[0]
    const time = parseInt(value.split('_')[1], 10)

    const form = Object.assign({}, this.form)
    const cancel_sections = form[this.uuid].data.cancel_sections
      .slice()
      .map(x => Object.assign({}, x))

    if (prop === 'to') {
      const isNextItem = cancel_sections[this.index + 1]

      if (isNextItem) {
        const nextAdvertised = this.places.find(
          x =>
            x.short_name === place &&
            x.advertised >= time &&
            x.type === 'Avgang'
        )?.advertised

        if (nextAdvertised) {
          cancel_sections[this.index + 1]['from'] = place
          cancel_sections[this.index + 1]['departure'] = nextAdvertised
        } else {
          cancel_sections[this.index + 1]['from'] = null
          cancel_sections[this.index + 1]['departure'] = null
        }
      }
    }

    cancel_sections[this.index][prop] = place
    cancel_sections[this.index][prop === 'to' ? 'arrival' : 'departure'] = time

    this.$store.dispatch('duaMessage/updateForm', {
      uuid: this.uuid,
      prop: 'cancel_sections',
      value: cancel_sections
    })

    this.$nextTick(() => {
      this.refForm.validate()
    })
  }
})

export default {
  name: 'DuaMessageRouteField',

  props: {
    index: Number,
    item: Object,
    uuid: String,
    validatingForm: Boolean,
    cancelSections: Array,
    refForm: null,
    loading: Boolean,
    searchTrain: null
  },

  data() {
    return { required }
  },

  methods: {
    format(date) {
      return format(new Date(date), 'HH:mm')
    },

    isDiff(prop) {
      if (!this.currentTrip) return false
      const currentBodySection = this.currentTrip.data?.cancel_sections?.[
        this.index
      ]
      const currentFormSection = this.form[this.uuid].data?.cancel_sections?.[
        this.index
      ]
      const currentProp = currentBodySection?.[prop] || null
      const formProp = currentFormSection?.[prop] || null
      const advertisedProp = prop === 'to' ? 'arrival' : 'departure'
      const currentTimeProp = currentBodySection?.[advertisedProp] || null
      const formTimeProp = currentFormSection?.[advertisedProp] || null
      return currentProp !== formProp || currentTimeProp !== formTimeProp
    },

    placeRule(v) {
      if (this.index === 0) return true
      const place = this.fromPlaces.find(
        x =>
          x.short_name === this.cancelSections[this.index - 1].to &&
          x.advertised >= this.cancelSections[this.index - 1].arrival
      )

      if (place && place.key === v) {
        return true
      }

      return 'Platsen måste börja på samma plats som föregående inställelsesträcka'
    }
  },

  computed: {
    isHistoryEvent: get('history/isHistoryEvent'),
    stops: get('event/stops'),
    form: get('duaMessage/form'),
    model: get('duaMessage/model'),

    replacement_from_place: {
      ...getSet('from')
    },

    replacement_to_place: {
      ...getSet('to')
    },

    disableFirstPlace() {
      return this.index === 0
    },

    disableLastPlace() {
      return this.index === this.cancelSections.length - 1
    },

    isChecked() {
      return this.form[this.uuid]._checked
    },

    currentTrip() {
      return this.model?.body?.find(x => x?.trip_uuid === this.uuid)
    },

    isAdvertisedPlaces() {
      return Boolean(
        this.item.text.type === 'RST' &&
          this.searchTrain &&
          this.searchTrain.places.length
      )
    },

    places() {
      if (this.isAdvertisedPlaces) {
        const filteredPlaces = this.searchTrain.places
          .map(x => ({
            type: x.type,
            key: `${x.location}_${+new Date(x.advertised)}`,
            advertised: +new Date(x.advertised),
            short_name: x.location,
            stop_name: x.location_long_name
          }))
          .sort((a, b) => a.advertised - b.advertised)

        const firstSplitKey = `${this.item.text.from}_${+new Date(
          this.item.text.departure
        )}`
        const lastSplitKey = `${this.item.text.to}_${+new Date(
          this.item.text.arrival
        )}`
        const firstSplitIndex = filteredPlaces.findIndex(
          x => x.key === firstSplitKey
        )
        const lastSplitIndex = filteredPlaces.findIndex(
          x => x.key === lastSplitKey
        )

        return filteredPlaces.slice(firstSplitIndex, lastSplitIndex + 1)
      }
      return this.stops.map((x, key) => ({ ...x, key }))
    },

    fromPlaces() {
      if (!this.isAdvertisedPlaces) return this.places
      return this.places.filter(x => x.type === 'Avgang')
      // console.log(this.places)
      // const departureDate = +new Date(this.item.text.departure)
      // const arrivalDate = +new Date(this.item.text.arrival)
      // const firstItemIndex = this.places.findIndex(
      //   x =>
      //     x.departure &&
      //     x.short_name === this.item.text.from &&
      //     +new Date(x.departure) >= departureDate
      // )
      // const lastItemIndex =
      //   this.places.findIndex(
      //     x =>
      //       x.short_name === this.item.text.to &&
      //       +new Date(x.arrival) <= arrivalDate &&
      //       +new Date(x.departure) > departureDate
      //   ) + 1

      // return this.places.slice(firstItemIndex, lastItemIndex)
    },

    toPlaces() {
      if (!this.isAdvertisedPlaces || !this.replacement_from_place)
        return this.places
      // const splitIndex = this.places.findIndex(
      //   x => x.short_name === this.replacement_from_place
      // )
      // return this.places.slice(splitIndex + 1)
      return this.places.filter(x => x.type === 'Ankomst')
    }
  }
}
</script>
